import { request } from '../http';

export type CustomerBillingOverview = {
  organization: { id: string; name: string; slug: string };
  currentPlan: CustomerBillingPlan | null;
  entitlement: { status: string | null; source: string | null; planSlug: string | null };
  status: string;
  provider: string;
  autoRenew: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  expiresAt: string | null;
  graceUntil: string | null;
  paymentStatus: string | null;
  lifecycleState: string | null;
  lifecycleLabel: string | null;
  onlinePaymentsAvailable: boolean;
  usage: {
    searches: { used: number; limit: number | null };
    recipients: { used: number; limit: number | null };
    notificationsToday: { used: number; limit: number | null };
  };
  actions: CustomerBillingActions;
};

export type CustomerBillingActions = {
  canUpgrade: boolean;
  canDowngrade: boolean;
  canCancel: boolean;
  canResume: boolean;
  canManagePaymentMethod: boolean;
  canRetryPayment: boolean;
  canChangePlan: boolean;
  canOpenPortal: boolean;
};

export type CustomerBillingPlan = {
  id?: string;
  slug: string;
  name?: string;
  description?: string;
  priceCents?: number;
  currency?: string;
  billingInterval?: string;
  maxSearches?: number;
  maxNotificationRecipients?: number;
  maxNotificationsPerDay?: number;
  minScrapeIntervalMinutes?: number;
  historyDays?: number;
  features?: string[];
  marketingTitle?: string;
  badge?: string;
  recommended?: boolean;
  displayOrder?: number;
  isCurrent?: boolean;
};

export type CustomerBillingInvoice = {
  id: string;
  provider: string;
  status: string;
  amountCents: number;
  currency: string;
  createdAt?: string | null;
  hostedInvoiceUrl?: string | null;
  invoicePdf?: string | null;
  description?: string | null;
};

export type CustomerBillingPayment = {
  id: string;
  createdAt?: string | null;
  amountCents: number;
  currency: string;
  status: string;
  provider: string;
  description?: string | null;
  receiptUrl?: string | null;
};

export type CustomerBillingEvent = {
  id: string;
  type: string;
  title: string;
  status?: string | null;
  message?: string | null;
  createdAt?: string | null;
  planSlug?: string | null;
};

export const customerBillingService = {
  overview: () => request<CustomerBillingOverview>('/customer-billing/overview'),
  plans: () => request<{ items: CustomerBillingPlan[] }>('/customer-billing/plans'),
  checkout: (planSlug: string) =>
    request<{ checkoutUrl: string; status: string }>('/customer-billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ planSlug }),
    }),
  portal: (returnUrl?: string) =>
    request<{ portalUrl: string; status: string }>('/customer-billing/portal', {
      method: 'POST',
      body: JSON.stringify(returnUrl ? { returnUrl } : {}),
    }),
  changePlan: (planSlug: string) =>
    request<{ status: string; message?: string; checkoutUrl?: string; portalUrl?: string }>(
      '/customer-billing/change-plan',
      {
        method: 'POST',
        body: JSON.stringify({ planSlug }),
      },
    ),
  cancel: () =>
    request<{
      status: string;
      cancelAtPeriodEnd?: boolean | null;
      currentPeriodEnd?: string | null;
      portalUrl?: string | null;
      message?: string;
    }>('/customer-billing/cancel', { method: 'POST', body: '{}' }),
  resume: () =>
    request<{ status: string; cancelAtPeriodEnd?: boolean; currentPeriodEnd?: string | null; message?: string }>(
      '/customer-billing/resume',
      { method: 'POST', body: '{}' },
    ),
  invoices: () => request<{ items: CustomerBillingInvoice[] }>('/customer-billing/invoices'),
  payments: () => request<{ items: CustomerBillingPayment[] }>('/customer-billing/payments'),
  events: () => request<{ items: CustomerBillingEvent[] }>('/customer-billing/events'),
  retryPayment: () =>
    request<{ status: string; message?: string; paymentUrl?: string; portalUrl?: string }>(
      '/customer-billing/retry-payment',
      { method: 'POST', body: '{}' },
    ),
};
